import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { db } from "../../config/firebase";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  CircularProgress,
  Box,
  TextField,
  Divider,
  Snackbar,
  Alert,
  Button,
  Container,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserSecret,
  faPenToSquare,
  faUserSlash,
  faXmark,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import EditAvatar from "../CcpAdmin/CcpAdminUsers/EditForm/Avatar";
import TpAddUserModal from "./TpAddUserModal";
import EditTpUser from "./EditTpUser";
import TpUserTypes from "./TpUserTypes";
import TpUserMgtBreadCrumbs from "../Layout/TpUserMgtBreadCrumbs";

const TpAdmins = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [userTypesModalOpen, setUserTypesModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);

  const fetchAdminUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const active = [];
      const inactive = [];

      users.forEach((user) => {
        // Check if user has techPrepAdminFlag or admin userType
        const hasLegacyFlag = user.techPrepAdminFlag === true;
        const hasAdminType = user.userTypes?.admin === true;

        if (hasLegacyFlag || hasAdminType) {
          // Check if user would be considered inactive
          const allUserTypesFalse =
            user.userTypes &&
            Object.values(user.userTypes).every(
              (value) => value === false || value === 0
            );
          const noUserTypes =
            !user.userTypes || Object.keys(user.userTypes).length === 0;
          const isInactive =
            hasLegacyFlag &&
            !hasAdminType &&
            (allUserTypesFalse || noUserTypes);

          if (isInactive) {
            inactive.push(user);
          } else {
            active.push(user);
          }
        }
      });

      setActiveUsers(active.sort((a, b) => a.name.localeCompare(b.name)));
      setInactiveUsers(inactive.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching admin users: ", error);
      setActiveUsers([]);
      setInactiveUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchAdminUsers();
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
    // Force a refresh to ensure any user type changes are reflected
    fetchAdminUsers();
  };

  const handleEditSuccess = () => {
    // Add a small delay to ensure Firestore changes are propagated
    setTimeout(() => {
      fetchAdminUsers();
    }, 500);
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAvatarUploadSuccess = () => {
    fetchAdminUsers();
  };

  const handlePasswordReset = async (user) => {
    const email = user.email;
    if (!email) {
      setSnackbarMessage("This user has no email on file.");
      setSnackbarOpen(true);
      return;
    }
    setResettingPasswordFor(user.id);
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setSnackbarMessage(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setSnackbarMessage(
        error.message || "Error sending password reset email",
      );
    } finally {
      setResettingPasswordFor(null);
      setSnackbarOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getUserTypeLabels = (user, isInactiveTab = false) => {
    const types = [];

    if (isInactiveTab) {
      types.push("Inactive");
    } else {
      // For active admin users
      if (
        user.userTypes?.admin === true ||
        (user.userTypes?.admin !== false && user.techPrepAdminFlag === true)
      ) {
        types.push("Admin");
      }
      if (user.userTypes?.coordinator) types.push("Coordinator");
      if (user.userTypes?.instructor) types.push("Instructor");
      if (user.userTypes?.staff) types.push("Staff");
      if (user.userTypes?.pathwayManager) types.push("Pathway Manager");
    }
    return types;
  };

  const renderUserGrid = (users, isInactiveTab = false) => {
    const filteredUsers = users.filter((user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <Grid container spacing={2} style={{ marginBottom: "50px" }}>
        {filteredUsers.map((user) => (
          <Grid item xs={12} sm={6} md={4} key={user.id}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar
                      src={user.avatar}
                      alt={user.name}
                      sx={{ width: 80, height: 80 }}
                      onAvatarUploadSuccess={handleAvatarUploadSuccess}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" component="div">
                      {user.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.phone}
                    </Typography>

                    {/* Display user type chips */}
                    <Box mt={1} mb={1}>
                      {getUserTypeLabels(user, isInactiveTab).map(
                        (type, index) => (
                          <Chip
                            key={index}
                            label={type}
                            size="small"
                            color={
                              type === "Admin"
                                ? "primary"
                                : type === "Inactive"
                                ? "default"
                                : "secondary"
                            }
                            sx={{
                              mr: 0.5,
                              mb: 0.5,
                              backgroundColor:
                                type === "Inactive" ? "#f5f5f5" : undefined,
                              color:
                                type === "Inactive" ? "#757575" : undefined,
                            }}
                          />
                        )
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <FontAwesomeIcon
                        icon={faPenToSquare}
                        style={{ color: "purple", cursor: "pointer" }}
                        size="2x"
                        onClick={() => handleEditClick(user)}
                      />
                      <FontAwesomeIcon
                        icon={faLock}
                        title="Send password reset email"
                        style={{
                          color:
                            resettingPasswordFor === user.id
                              ? "#bbb"
                              : "#2b7cff",
                          cursor:
                            resettingPasswordFor === user.id
                              ? "default"
                              : "pointer",
                        }}
                        size="2x"
                        onClick={() =>
                          resettingPasswordFor === user.id
                            ? null
                            : handlePasswordReset(user)
                        }
                      />
                      <EditAvatar
                        user={user}
                        onAvatarUploadSuccess={handleAvatarUploadSuccess}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredUsers.length === 0 && (
          <Grid item xs={12}>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No {isInactiveTab ? "inactive" : "active"} admin users found
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ paddingTop: "80px" }}>
      <TpUserMgtBreadCrumbs />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
          <FontAwesomeIcon
            icon={faUserSecret}
            style={{
              color: "#f44336",
              fontSize: "2rem",
              marginRight: "16px",
            }}
          />
          <Typography variant="h4" component="div" gutterBottom>
            Tech Prep Admins
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <TpAddUserModal onSuccess={fetchAdminUsers} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setUserTypesModalOpen(true)}
          >
            Usertypes
          </Button>
        </Box>
        <TpUserTypes
          open={userTypesModalOpen}
          onClose={() => setUserTypesModalOpen(false)}
        />
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={`Active (${activeUsers.length})`}
            icon={<FontAwesomeIcon icon={faUserSecret} />}
            iconPosition="start"
          />
          <Tab
            label={`Inactive (${inactiveUsers.length})`}
            icon={<FontAwesomeIcon icon={faUserSlash} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h6" color="text.secondary">
          {tabValue === 0
            ? `${
                activeUsers.filter((user) =>
                  user.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length
              } active admin user${
                activeUsers.filter((user) =>
                  user.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length !== 1
                  ? "s"
                  : ""
              } found`
            : `${
                inactiveUsers.filter((user) =>
                  user.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length
              } inactive admin user${
                inactiveUsers.filter((user) =>
                  user.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length !== 1
                  ? "s"
                  : ""
              } found`}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search Admin Users"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <IconButton
            onClick={handleClearSearch}
            size="small"
            title="Clear search"
            disabled={!searchTerm}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </Box>

      <Divider style={{ marginBottom: "20px" }} />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tabValue === 0 && renderUserGrid(activeUsers, false)}
          {tabValue === 1 && renderUserGrid(inactiveUsers, true)}
        </>
      )}

      {selectedUser && (
        <EditTpUser
          open={editModalOpen}
          onClose={handleEditModalClose}
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onUserDeleted={() => {
            fetchAdminUsers();
            setSnackbarMessage("User has been deleted");
            setSnackbarOpen(true);
            setEditModalOpen(false);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%", backgroundColor: "black", color: "white" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
  );
};

export default TpAdmins;
